import React, { useState, useRef, useEffect, useCallback } from "react";
import Webcam from "react-webcam";
import logger from '../utils/logger';
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { usePageTracking, useAnalytics } from "../hooks/useAnalytics";
import LoadingScreen from "../components/LoadingScreen";
import PandaLoader from "../components/PandaLoader";
import { showToast } from "../components/Toast";
import backgroundTaskManager from "../utils/backgroundTasks";
import { supabase } from "../lib/supabaseClient";
import PaywallModal from "../components/PaywallModal";
import VoicePicker from "../components/character/VoicePicker";
import VoiceRecorder from "../components/character/VoiceRecorder";
import { useEntitlementGate } from "../contexts/EntitlementGateContext";
import { saveCreationDraft, takeCreationDraft, fileFromDataUrl } from "../utils/creationDraft";
import {
  compressImage,
  MAX_CHARACTER_IMAGE_BYTES,
} from "../utils/imageCompression";

// ── Interactive gradient placeholder for character generation ──
const CharacterCreatingPlaceholder = ({ size = "full" }) => {
  const isFull = size === "full";

  if (!isFull) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ background: 'transparent' }}>
        <PandaLoader variant="inline" size={220} />
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        paddingTop: 120,
        background: 'linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}
    >
      <PandaLoader variant="inline" size={320} />
      <h2
        style={{
          margin: '-0.25rem 0 0',
          fontFamily: "'Caveat', cursive",
          color: '#0065F8',
          fontWeight: 700,
          fontSize: '3rem',
          lineHeight: 1.1,
          textAlign: 'center',
        }}
      >
        Loading...
      </h2>
      <p
        style={{
          marginTop: 12,
          fontFamily: "'Fredoka', sans-serif",
          color: '#5A6B85',
          fontSize: 15,
          textAlign: 'center',
          maxWidth: 480,
          padding: '0 16px',
        }}
      >
        Your character is being created — this usually takes about 30 seconds.
      </p>
    </div>
  );
};

const CharacterLab = () => {
  usePageTracking('Character Lab');
  const { trackCharacterAnimation, trackCharacterCreationStarted, trackCharacterCreation, trackError } = useAnalytics();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const characterId = searchParams.get("id");
  const { apiClient } = useAPI();
  const {
    user,
    subscriptionStatus,
    refreshSubscriptionStatus,
    waitForSubscriptionActivation,
    costs,
    updateUser,
  } = useAuth();
  const { requireEntitlement } = useEntitlementGate();
  // Wall-interrupted draft (prompt / upload / sketch); restored when
  // checkout returns to this page.
  const labDraftRef = useRef(takeCreationDraft("character-lab"));

  const { currentProfile, requireParentalVerification } = useProfile();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(5);
  const [brushColor, setBrushColor] = useState("#000000");
  const [characterName, setCharacterName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [drawingHistory, setDrawingHistory] = useState([]);
  const [redoHistory, setRedoHistory] = useState([]);
  const [isProcessingAnimation, setIsProcessingAnimation] = useState(false);
  const previewCanvasRef = useRef(null);
  const [showAnimated, setShowAnimated] = useState(false);
  const [animatedImageUrl, setAnimatedImageUrl] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [handdrawnImageUrl, setHanddrawnImageUrl] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isUploadImageLoading, setIsUploadImageLoading] = useState(false);
  const [inputMode, setInputMode] = useState(null); // null (prompt), 'draw', or 'upload'
  const [promptText, setPromptText] = useState('');
  const [promptImage, setPromptImage] = useState(null);
  const [promptImagePreview, setPromptImagePreview] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [generatingCharacterId, setGeneratingCharacterId] = useState(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [generatingName, setGeneratingName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  const webcamRef = useRef(null);
  const promptFileInputRef = useRef(null);
  const [isShuffling, setIsShuffling] = useState(false);

  // Draft save/restore AFTER inputMode (+ related state) are declared —
  // dependency arrays evaluate during render and would TDZ-crash otherwise.
  // Paywall / requireEntitlement behavior is unchanged.
  const saveLabDraft = () => {
    let drawingDataUrl = null;
    try {
      if (canvasRef.current) drawingDataUrl = canvasRef.current.toDataURL("image/png");
    } catch { /* tainted canvas — skip the sketch */ }
    saveCreationDraft("character-lab", {
      promptText,
      characterName,
      inputMode,
      promptImagePreview: promptImagePreview || null,
      drawingDataUrl,
    });
  };

  useEffect(() => {
    const d = labDraftRef.current;
    if (!d) return;
    if (d.promptText) setPromptText(d.promptText);
    if (d.characterName) setCharacterName(d.characterName);
    if (d.inputMode) setInputMode(d.inputMode);
    if (d.promptImagePreview) {
      setPromptImagePreview(d.promptImagePreview);
      fileFromDataUrl(d.promptImagePreview, "restored-upload.png")
        .then(setPromptImage)
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redraw the saved sketch once the draw-mode canvas exists
  useEffect(() => {
    const d = labDraftRef.current;
    if (!d?.drawingDataUrl || inputMode !== "draw") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const img = new Image();
    img.onload = () => {
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = d.drawingDataUrl;
    labDraftRef.current = { ...d, drawingDataUrl: null };
  }, [inputMode]); // eslint-disable-line react-hooks/exhaustive-deps


  const generateRandomPrompt = async () => {
    setIsShuffling(true);
    try {
      const resp = await apiClient.request('/generate-character-prompt');
      const prompt = resp?.data?.prompt || resp?.prompt;
      if (prompt) setPromptText(prompt);
      else showToast('Failed to generate prompt', 'error');
    } catch (e) {
      showToast('Failed to generate prompt', 'error');
    } finally {
      setIsShuffling(false);
    }
  };

  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [autoAnimate, setAutoAnimate] = useState(false);
  const [autoCreateFromImage, setAutoCreateFromImage] = useState(null);
  const autoCreateMethodRef = useRef('upload');
  const fileInputRef = useRef(null);
  const uploadBtnRef = useRef(null);

  // Character editing state
  const [createdCharacterId, setCreatedCharacterId] = useState(null);
  const [pendingCreation, setPendingCreation] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [savedCharacterData, setSavedCharacterData] = useState(null);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [paywallStarCost, setPaywallStarCost] = useState(0);
  const [paywallCurrentStars, setPaywallCurrentStars] = useState(0);
  const [editPrompt, setEditPrompt] = useState("");
  const [isProcessingEdit, setIsProcessingEdit] = useState(false);
  
  // Track number of edits made (max 3 edits per character)
  const [editCount, setEditCount] = useState(0);
  
  // Display image URL - what both left and right panels show (fully local, no DB updates)
  const [displayImageUrl, setDisplayImageUrl] = useState(null);

  // State for showing edit panel
  const [showEditPanel, setShowEditPanel] = useState(false);
  
  // State for save modal
  const [showSaveModal, setShowSaveModal] = useState(false);
  // Voice picked in the Save window: { type: 'preset', voice_id, name } |
  // { type: 'random' } | { type: 'recorded', file }. null until the best
  // match for this picture arrives (the server auto-picks if it never does).
  const [voiceChoice, setVoiceChoice] = useState(null);
  const [voiceRecorderOpen, setVoiceRecorderOpen] = useState(false);
  useEffect(() => { setVoiceChoice(null); }, [previewId]);

  const applyPreviewResponse = useCallback((response) => {
    console.log("PREVIEW RESPONSE:", response);
    if (
      !response ||
      (response.status !== "preview" &&
        response.status !== "success" &&
        response.status !== true)
    ) {
      throw new Error(response?.message || "Character preview failed");
    }

    const image =
      response?.animated_image_url ||
      response?.image_url ||
      response?.data?.animated_image_url ||
      response?.data?.image_url;
    if (!image) {
      throw new Error(response?.message || "Character preview failed");
    }

    const resolvedPreviewId =
      response?.preview_id || response?.data?.preview_id || null;
    console.log("IMAGE URL:", image);
    setPreviewImage(image);
    setPreviewId(resolvedPreviewId);
    setShowPreview(true);

    // Keep existing workspace rendering in sync.
    setAnimatedImageUrl(image);
    setGeneratedImageUrl(image);
    setShowAnimated(true);
    setInputMode("upload");
    setShowNameInput(false);
    setGeneratingName("");
    setGeneratingCharacterId(null);
  }, []);

  // Initialize canvas and load subscription limits
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && canvas.getContext) {
      try {
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.lineCap = "round";
          ctx.lineJoin = "round";

          // Save initial state
          setDrawingHistory([
            ctx.getImageData(0, 0, canvas.width, canvas.height),
          ]);
        }
      } catch (error) {
        logger.error("Error initializing canvas:", error);
      }
    }

    // Check for upgrade parameter and refresh subscription data
    const upgraded = searchParams.get("upgraded");
    const cancelled = searchParams.get("cancelled");

    if (upgraded === "true") {
      showToast(
        "Payment successful! Your subscription has been updated.",
        "success"
      );
      setSearchParams({});
      // Poll until RevenueCat has ingested the new subscription
      waitForSubscriptionActivation();
    } else if (cancelled === "true") {
      showToast("Payment was cancelled.", "info");
      setSearchParams({});
    }
  }, [apiClient, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set displayImageUrl when character is loaded (for existing characters or after animation)
  useEffect(() => {
    if (animatedImageUrl) {
      setDisplayImageUrl(animatedImageUrl);
    }
  }, [animatedImageUrl]);

  // Fetch character details when characterId is present in URL (edit mode)
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchCharacter = async () => {
      if (!characterId || !apiClient) return;

      try {
        setIsUploadImageLoading(true);
        const response = await apiClient.getCharacter(characterId);
        
        // Check if component is still mounted before updating state
        if (!isMounted) return;
        
        if (response.success || response.status) {
          const charData = response.data || response;
          console.log("📦 Character data loaded:", {
            name: charData.name,
            animated_image_url: charData.animated_image_url,
            handdrawn_image_url: charData.handdrawn_image_url,
            edit_count: charData.edit_count
          });
          setCharacterName(charData.name || "");
          // Preserve original image URLs - don't overwrite with edited versions
          setUploadedImageUrl(charData.handdrawn_image_url || "");
          setHanddrawnImageUrl(charData.handdrawn_image_url || "");
          setAnimatedImageUrl(charData.animated_image_url || "");
          // Set displayImageUrl immediately (what both panels will show)
          setDisplayImageUrl(charData.animated_image_url || "");
          setShowAnimated(true); // always show animated in edit mode
          setCreatedCharacterId(characterId);
          // Don't set inputMode for edit mode - it will be handled by characterId check

          // Set edit count if available (max 3 edits per character)
          if (charData.edit_count !== undefined) {
            setEditCount(charData.edit_count || 0);
          } else {
            setEditCount(0);
          }

          // Preview will be drawn automatically by the main preview useEffect
          // when animatedImageUrl is set above
        } else {
          if (isMounted) {
            setIsUploadImageLoading(false);
            showToast("Failed to load character", "error");
          }
        }
      } catch (error) {
        if (isMounted) {
          setIsUploadImageLoading(false);
          console.error("Error fetching character:", error);
          showToast("Error loading character", "error");
        }
      }
    };

    fetchCharacter();

    // Cleanup function
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [characterId, apiClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper to get coordinates from mouse or touch event
  const getCoordinates = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    // Get coordinates from touch event or mouse event
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // Scale coordinates to account for canvas scaling
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    return { x, y };
  }, []);

  // Drawing functions
  const startDrawing = useCallback(
    (e) => {
      const canvas = canvasRef.current;
      if (!canvas || !canvas.getContext) return;

      try {
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Ensure canvas has proper dimensions and white background
        if (canvas.width === 0 || canvas.height === 0) {
          canvas.width = 400;
          canvas.height = 400;
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
        }

        const { x, y } = getCoordinates(e);

        setIsDrawing(true);
        ctx.strokeStyle = brushColor;
        ctx.lineWidth = brushSize;
        ctx.beginPath();
        ctx.moveTo(x, y);
      } catch (error) {
        logger.error("Error starting drawing:", error);
      }
    },
    [brushColor, brushSize, getCoordinates]
  );

  const draw = useCallback(
    (e) => {
      if (!isDrawing) return;

      const canvas = canvasRef.current;
      if (!canvas || !canvas.getContext) return;

      try {
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const { x, y } = getCoordinates(e);

        ctx.lineTo(x, y);
        ctx.stroke();
      } catch (error) {
        logger.error("Error drawing:", error);
      }
    },
    [isDrawing, getCoordinates]
  );

  const stopDrawing = useCallback(() => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      if (canvas && canvas.getContext) {
        try {
          const ctx = canvas.getContext("2d");
          if (ctx) {
            const imageData = ctx.getImageData(
              0,
              0,
              canvas.width,
              canvas.height
            );
            setDrawingHistory((prev) => [...prev.slice(-9), imageData]);
            setRedoHistory([]); // Clear redo history on new stroke
          }
        } catch (error) {
          logger.error("Error saving drawing state:", error);
        }
      }
    }
  }, [isDrawing]);

  const clearCanvas = (resetHistory = false) => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.getContext) return;

    try {
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const clearedState = ctx.getImageData(0, 0, canvas.width, canvas.height);
      
      if (resetHistory) {
        // Full reset - used when switching modes or starting fresh
        setDrawingHistory([clearedState]);
      } else {
        // Add to history - makes clear undoable
        setDrawingHistory(prev => [...prev.slice(-9), clearedState]);
      }
      setRedoHistory([]); // Clear redo history on clear
    } catch (error) {
      logger.error("Error clearing canvas:", error);
    }
  };

  const undoDrawing = () => {
    if (drawingHistory.length > 1) {
      const lastState = drawingHistory[drawingHistory.length - 1];
      const newHistory = drawingHistory.slice(0, -1);
      setDrawingHistory(newHistory);
      setRedoHistory(prev => [...prev, lastState]);

      const canvas = canvasRef.current;
      if (canvas && canvas.getContext) {
        try {
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.putImageData(newHistory[newHistory.length - 1], 0, 0);
          }
        } catch (error) {
          logger.error("Error undoing drawing:", error);
        }
      }
    }
  };

  const redoDrawing = () => {
    if (redoHistory.length > 0) {
      const stateToRedo = redoHistory[redoHistory.length - 1];
      setRedoHistory(prev => prev.slice(0, -1));
      setDrawingHistory(prev => [...prev, stateToRedo]);

      const canvas = canvasRef.current;
      if (canvas && canvas.getContext) {
        try {
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.putImageData(stateToRedo, 0, 0);
          }
        } catch (error) {
          logger.error("Error redoing drawing:", error);
        }
      }
    }
  };

  const hasDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.getContext) return false;

    try {
      const ctx = canvas.getContext("2d");
      if (!ctx) return false;

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        if (data[i] !== 255 || data[i + 1] !== 255 || data[i + 2] !== 255) {
          return true;
        }
      }
      return false;
    } catch (error) {
      logger.error("Error checking drawing:", error);
      return false;
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showToast("Please select a valid image file", "error");
        return;
      }

      if (file.size > MAX_CHARACTER_IMAGE_BYTES) {
        showToast(
          `This image is over ${(MAX_CHARACTER_IMAGE_BYTES / (1024 * 1024)).toFixed(0)} MB. Please choose a smaller file.`,
          "error"
        );
        return;
      }

      try {
        const compressedFileWithName = await compressImage(file, {
          outputFileName: file.name,
        });

        logger.log(
          `📦 Image compressed: ${(file.size / 1024).toFixed(1)}KB → ${(
            compressedFileWithName.size / 1024
          ).toFixed(1)}KB`
        );

        setUploadedImage(compressedFileWithName);
        setAutoAnimate(true); // Auto-trigger animation after upload

        const reader = new FileReader();
        reader.onload = (e) => {
          setUploadedImageUrl(e.target.result);
        };
        reader.readAsDataURL(compressedFileWithName);
      } catch (error) {
        logger.error("Error compressing image:", error);
        showToast("Failed to optimize image. Please try again.", "error");
      }
    }
  };

  const removeUploadedImage = () => {
    setUploadedImage(null);
    setUploadedImageUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Auto-trigger character creation after image upload (skip the preview page)
  useEffect(() => {
    if (autoAnimate && uploadedImage && !isProcessingAnimation) {
      setAutoAnimate(false);
      animateImage();
    }
  }, [autoAnimate, uploadedImage]);

  // Auto-preview character from camera/upload image (no save, no stars)
  useEffect(() => {
    if (!autoCreateFromImage || isCreating) return;
    const imageFile = autoCreateFromImage;
    const previewMethod = autoCreateMethodRef.current;
    setAutoCreateFromImage(null);
    autoCreateMethodRef.current = 'upload'; // reset
    // Members-only gate — previews are the expensive generation step
    if (!requireEntitlement({ trigger: "creation", contentType: "character" })) { saveLabDraft(); return; }

    (async () => {
      try {
        setIsCreating(true);
        const compressedImage = await compressImage(imageFile, { outputFileName: imageFile.name });
        trackCharacterCreationStarted({ method: previewMethod, hasImage: true });
        const response = await apiClient.previewCharacterFromPrompt({
          user_id: user?.id,
          image: compressedImage,
          handdrawn: false,
        });
        applyPreviewResponse(response);
        showToast("Preview generated. Click Save to create your character.", "success");
      } catch (error) {
        showToast(error.message || 'Character preview failed', 'error');
        setIsCreating(false);
      } finally {
        setIsCreating(false);
      }
    })();
  }, [autoCreateFromImage, applyPreviewResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  const animateImage = async () => {
    let imageBlob;
    let isHanddrawn = true;
    const userId = user?.id;
    if (!userId) {
      console.error("❌ Missing user_id");
      showToast("Please sign in again.", "error");
      return;
    }
    // Members-only gate — previews are the expensive generation step
    if (!requireEntitlement({ trigger: "creation", contentType: "character" })) { saveLabDraft(); return; }

    if (inputMode === "draw") {
      if (!hasDrawing()) {
        showToast(
          "Please draw something on the canvas before animating",
          "error"
        );
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) {
        showToast("Please draw something first", "error");
        return;
      }
      if (!canvas.getContext) {
        showToast("Canvas not available", "error");
        return;
      }

      // Ensure the canvas has a white background before export
      const ctx = canvas.getContext("2d");
      const originalCompositeOperation = ctx.globalCompositeOperation;
      ctx.globalCompositeOperation = "destination-over";
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = originalCompositeOperation;

      const imageFile = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Canvas conversion failed"));
              return;
            }
            resolve(
              new File([blob], "drawing.png", {
                type: "image/png",
                lastModified: Date.now(),
              })
            );
          },
          "image/png",
          1.0
        );
      });
      imageBlob = imageFile;
      setHanddrawnImageUrl(canvas.toDataURL("image/png"));
      isHanddrawn = true;
    } else {
      if (!uploadedImage) {
        showToast("Please upload an image before animating", "error");
        return;
      }
      imageBlob = uploadedImage;
      isHanddrawn = false;
    }

    try {
      setIsProcessingAnimation(true);
      const startTime = Date.now();
      const animateResult = await apiClient.animateImageData(
        imageBlob,
        isHanddrawn,
        userId
      );
      const duration = Math.round((Date.now() - startTime) / 1000);

      logger.log("🎨 Animation result:", animateResult);

      // Check for SAFETY_BLOCK error
      if (animateResult.isSafetyBlock || animateResult.error === "SAFETY_BLOCK") {
        showToast(
          "This image isn't suitable for kids' stories. Please upload or draw a child-friendly character.",
          "error",
          6000
        );
        trackCharacterAnimation('character_lab', false);
        trackError('safety_block', 'Image blocked by safety filter', { source: 'character_lab' });
        return;
      }

      applyPreviewResponse(animateResult);
      setEditCount(0); // Reset edit count for new character
      showToast("Preview generated. Click Save to create your character.", "success");
      trackCharacterAnimation('character_lab', true, duration);
    } catch (error) {
      logger.error("Error animating image:", error);

      // Trial wall already opened via the 403 backstop event — no toast.
      if (error?.code === "SUBSCRIPTION_REQUIRED") return;

      // Check if it's a moderation error
      const isModerationError = error.message?.includes('safety_violations') ||
                                error.message?.includes('moderation_blocked');

      if (isModerationError) {
        showToast(
          "The AI safety system flagged your drawing. This sometimes happens by mistake. Please try drawing something slightly different!",
          "error"
        );
      } else {
        showToast(`Animation failed: ${error.message}`, "error");
      }

      // Track failed animation
      trackCharacterAnimation('character_lab', false);
      trackError('animation_error', error.message, { source: 'character_lab' });
    } finally {
      setIsProcessingAnimation(false);
    }
  };

  useEffect(() => {
      const previewCanvas = previewCanvasRef.current;
    if (!previewCanvas) return;

        const previewCtx = previewCanvas.getContext("2d");
    if (!previewCtx) return;

    // Ensure canvas has dimensions (match JSX dimensions: 300x300)
    if (previewCanvas.width === 0 || previewCanvas.height === 0) {
      previewCanvas.width = 300;
      previewCanvas.height = 300;
      }

    // Helper to draw any image onto the preview
    const drawImageToPreview = (src) => {
      if (!src) return;
      
        const img = new Image();
      
      // Handle CORS for external URLs
      if (src.startsWith("http://") || src.startsWith("https://")) {
        img.crossOrigin = "anonymous";
      }
      
        img.onload = () => {
        try {
          previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
          previewCtx.fillStyle = "#ffffff";
          previewCtx.fillRect(0, 0, previewCanvas.width, previewCanvas.height);
          previewCtx.drawImage(
            img,
            0,
            0,
            previewCanvas.width,
            previewCanvas.height
          );
        } catch (error) {
          logger.error("Error drawing image to preview:", error);
          // Fallback: show white background
          previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
          previewCtx.fillStyle = "#ffffff";
          previewCtx.fillRect(0, 0, previewCanvas.width, previewCanvas.height);
        }
      };
      
      img.onerror = (error) => {
        logger.error("Error loading image for preview:", error, "URL:", src);
        // Show white background on error
          previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
        previewCtx.fillStyle = "#ffffff";
        previewCtx.fillRect(0, 0, previewCanvas.width, previewCanvas.height);
        };
      
      img.src = src;
    };

    // 1️⃣ If "Animated" toggle is ON and we have animated image
    if (showAnimated && animatedImageUrl) {
      drawImageToPreview(animatedImageUrl);
      }
    // 2️⃣ If "Original" toggle is ON
    else if (!showAnimated) {
      // Handle edit mode (characterId exists but inputMode is null)
      if (characterId) {
        // In edit mode, show the original handdrawn/uploaded image
        if (handdrawnImageUrl) {
          drawImageToPreview(handdrawnImageUrl);
        } else if (uploadedImageUrl) {
          drawImageToPreview(uploadedImageUrl);
    } else {
          // Default empty white background
          previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
          previewCtx.fillStyle = "#ffffff";
          previewCtx.fillRect(0, 0, previewCanvas.width, previewCanvas.height);
        }
      } else if (inputMode === "upload" && uploadedImageUrl) {
        drawImageToPreview(uploadedImageUrl);
      } else if (inputMode === "draw" && handdrawnImageUrl) {
        drawImageToPreview(handdrawnImageUrl);
      } else if (inputMode === "draw") {
        // Live drawing mode before animation
        const mainCanvas = canvasRef.current;
        if (mainCanvas) {
          previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
          previewCtx.fillStyle = "#ffffff";
          previewCtx.fillRect(0, 0, previewCanvas.width, previewCanvas.height);
          previewCtx.drawImage(mainCanvas, 0, 0, previewCanvas.width, previewCanvas.height);
        }
      } else {
        // Default empty white background
        previewCtx.clearRect(0, 0, previewCanvas.width, previewCanvas.height);
        previewCtx.fillStyle = "#ffffff";
        previewCtx.fillRect(0, 0, previewCanvas.width, previewCanvas.height);
      }
    }
  }, [
    showAnimated,
    animatedImageUrl,
    uploadedImageUrl,
    handdrawnImageUrl,
    inputMode,
    drawingHistory,
    characterId,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!previewId) {
      showToast("No preview found. Please generate a character first.", "error");
      return;
    }

    try {
      await proceedWithCharacterCreation();
    } catch (error) {
      logger.error('Error in handleSubmit:', error);
      setIsCreating(false);
      setPendingCreation(false);
    }
  };

  const proceedWithCharacterCreation = async () => {
    try {
      setIsCreating(true);

      const fallbackName =
        promptText.trim().slice(0, 20) ||
        generatingName.trim().slice(0, 20) ||
        "Character";
      const finalCharacterName = characterName.trim() || fallbackName;

      const characterData = {
        user_id: user?.id,
        profile_id: currentProfile?.id || null,
        preview_id: previewId,
        name: finalCharacterName,
        voice_choice: voiceChoice?.type === 'recorded' ? { type: 'cloned' } : voiceChoice,
      };
      const recordedVoice = voiceChoice?.type === 'recorded' ? voiceChoice.file : null;

      logger.log("📤 Creating character with data:", {
        user_id: characterData.user_id,
        profile_id: characterData.profile_id,
        preview_id: characterData.preview_id,
        name: characterData.name,
      });

      if (!characterData.user_id) {
        throw new Error(
          "User ID is missing. Please make sure you are logged in."
        );
      }
      if (!characterData.preview_id) {
        throw new Error("No preview found");
      }

      logger.log(
        "🚀 Creating character with backend handling description async..."
      );
      const response = await apiClient.createCharacter(characterData);
      console.log("CREATE RESPONSE:", response);

      logger.log("📦 Create character response:", response);

      // Check for name safety error first
      if (response.status === "name_inappropriate" || response.error === "name_inappropriate") {
        const errorMessage = response.message || "This name contains inappropriate content. Please choose a different name.";
        showToast(errorMessage, "error");
        logger.log("🚫 Name safety check failed - only clearing name field, keeping images");
        // Only clear the name field, keep images and other state intact
        setCharacterName("");
        setIsCreating(false);
        setPendingCreation(false);
        return;
      }

      if (response?.status === "success") {
        const responseData = response.data || response;
        const createdCharacter = Array.isArray(responseData)
          ? responseData[0]
          : responseData;

        logger.log("📦 Full response from backend:", response);
        logger.log("📦 Created character object:", createdCharacter);

        const characterId = createdCharacter?.id || response?.character_id || responseData?.character_id;
        if (response?.remaining_stars !== undefined) {
          updateUser({ ...user, pixley_stars: response.remaining_stars });
        }

        if (characterId) {
          logger.log("📝 Character created with ID:", characterId);
          setCreatedCharacterId(characterId);

          // Their recorded voice: the character exists now, so clone it
          // (the first recording is free). Runs behind the success screen.
          if (recordedVoice) {
            showToast(`Making ${finalCharacterName}'s voice from your recording…`, "info");
            apiClient.cloneCharacterVoice(characterId, [recordedVoice]).then((r) => {
              if (r?.status === "success") {
                showToast(`${finalCharacterName} now speaks with your voice!`, "success");
              } else {
                showToast(r?.detail || r?.error || r?.message || "Couldn't use that recording — you can record again on the character's page.", "error");
              }
            }).catch(() => {
              showToast("Couldn't use that recording — you can record again on the character's page.", "error");
            });
          }

          // Add CREATECHARACTER_<CHARACTER_ID> tag when character is created
          if (user?.id && apiClient) {
            const tag = `CREATECHARACTER_${characterId}`;
            apiClient.addUserDataTag(user.id, tag).then((result) => {
              if (result.status) {
                logger.log(`✅ Added tag ${tag} for user ${user.id}`);
              } else {
                logger.warn(`⚠️ Failed to add tag ${tag}:`, result.error);
              }
            }).catch((error) => {
              logger.error(`❌ Error adding tag ${tag}:`, error);
            });
          }

          // Show success screen instead of toast
          setSavedCharacterData({
            id: characterId,
            name: finalCharacterName,
            imageUrl: animatedImageUrl
          });
          setShowSuccessScreen(true);
        } else {
          logger.log("⚠️ No character ID in response:", createdCharacter);
          navigate("/characters");
        }

        // Set URLs from backend response
        if (createdCharacter.handdrawn_image_url) {
          setHanddrawnImageUrl(createdCharacter.handdrawn_image_url);
        }

        if (createdCharacter.animated_image_url) {
          setAnimatedImageUrl(createdCharacter.animated_image_url);
        }

        setCharacterName("");
        if (inputMode === "draw") {
          clearCanvas(true); // Reset history after saving
        } else {
          removeUploadedImage();
        }
        setShowAnimated(true);
        setPreviewId(null);
        setShowPreview(false);
        setPreviewImage(null);
        setShowSaveModal(false);
      } else {
        showToast(response?.message || "Character creation failed", "error");
      }
    } catch (error) {
      logger.error("Error creating character:", error);
      const errorMessage = error?.message || error?.toString() || "Unknown error occurred";
      showToast(
        errorMessage.includes("stars")
          ? errorMessage
          : `Failed to create character: ${errorMessage}. Please try again.`,
        "error"
      );
    } finally {
      setIsCreating(false);
    }
  };

  const compressImageDataUrl = (dataUrl, maxSizeKB = 800) => {
    return new Promise((resolve, reject) => {
      // Check if it's an external URL (not a data URL)
      const isExternalUrl =
        dataUrl.startsWith("http://") || dataUrl.startsWith("https://");

      const img = new Image();

      // Only set crossOrigin for external URLs, and handle CORS failure
      if (isExternalUrl) {
        img.crossOrigin = "anonymous";
      }

      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let { width, height } = img;
          const maxDimension = 512;

          if (width > height) {
            if (width > maxDimension) {
              height = (height * maxDimension) / width;
              width = maxDimension;
            }
          } else {
            if (height > maxDimension) {
              width = (width * maxDimension) / height;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          let quality = 0.8;
          let compressedDataUrl = canvas.toDataURL("image/jpeg", quality);

          while (
            compressedDataUrl.length > (maxSizeKB * 1024 * 4) / 3 &&
            quality > 0.1
          ) {
            quality -= 0.1;
            compressedDataUrl = canvas.toDataURL("image/jpeg", quality);
          }

          logger.log(
            `🗜️ Compressed image from ${Math.round(
              dataUrl.length / 1024
            )}KB to ${Math.round(compressedDataUrl.length / 1024)}KB`
          );
          resolve(compressedDataUrl);
        } catch (error) {
          // Canvas is tainted, return original
          logger.warn("Canvas tainted, using original URL:", error);
          resolve(dataUrl);
        }
      };

      img.onerror = () => {
        // If loading fails (e.g., CORS blocked), return the original dataUrl
        logger.warn("Failed to load image for compression, using original");
        resolve(dataUrl);
      };

      img.src = dataUrl;
    });
  };

  const editImagePreview = async () => {
    if (!animatedImageUrl) {
      return;
    }

    if (!editPrompt.trim()) {
      showToast("Please enter an edit prompt", "error");
      return;
    }

    // Check if edit limit reached (max 3 edits per character)
    if (editCount >= 3) {
      showToast("You've reached the maximum of 3 edits per character", "error");
      return;
    }

    // Members-only gate — edits are generation-backed
    if (!requireEntitlement({ trigger: "creation", contentType: "character" })) return;

    try {
      setIsProcessingEdit(true);

      let editResult;

      // Use editCharacterPreview for both newly created characters and existing characters
      // This ensures auto-saving works for all characters
      if (createdCharacterId && !characterId) {
        // Newly created character - use editCharacterPreview (auto-saves)
        editResult = await apiClient.editCharacterPreview(
          createdCharacterId,
          editPrompt.trim()
        );
      } else if (characterId) {
        // Existing character from URL - use editCharacterPreview (auto-saves)
        editResult = await apiClient.editCharacterPreview(
          characterId,
          editPrompt.trim()
        );
      } else {
        // Fallback: no character ID available, use editImagePreview (preview only)
        let imageDataUrl = animatedImageUrl;

        // If it's an external URL (DALL-E), fetch it and convert to data URL
        if (
          animatedImageUrl &&
          (animatedImageUrl.startsWith("http://") ||
          animatedImageUrl.startsWith("https://"))
        ) {
          try {
            const response = await fetch(animatedImageUrl);
            const blob = await response.blob();
            imageDataUrl = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
          } catch (fetchError) {
            logger.error("Failed to fetch external image:", fetchError);
            throw new Error(
              "Unable to fetch image for editing. Please try again."
            );
          }
        }

        // Now compress the data URL
        const compressedImageUrl = await compressImageDataUrl(imageDataUrl);
        
        // Convert data URL to File for API
        const dataURLToFile = (dataURL, filename) => {
          const arr = dataURL.split(",");
          const mime = arr[0].match(/:(.*?);/)[1];
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);
          while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
          }
          return new File([u8arr], filename, { type: mime });
        };
        
        const imageFile = dataURLToFile(compressedImageUrl, "edit-preview.jpg");
        editResult = await apiClient.editImagePreview(
          imageFile,
          editPrompt.trim()
        );
      }

      if (editResult.success || editResult.status) {
        // Handle both URL and base64 responses from backend - check all possible locations
        let editedUrl =
          editResult.edited_url ||
          editResult.edited_image ||
          editResult.data?.edited_url ||
          editResult.data?.edited_image ||
          editResult.data?.data?.edited_image;
        
        logger.log("🎨 Edit result:", {
          success: editResult.success,
          status: editResult.status,
          editedUrl: editedUrl,
          fullResult: editResult
        });
        
        if (editedUrl) {
          // If it's a base64 string without data URL prefix, add it
          if (editedUrl.startsWith("/9j/") || editedUrl.startsWith("iVBORw0KGgo")) {
            // It's a base64 string, determine MIME type
            const mimeType = editedUrl.startsWith("/9j/") ? "image/jpeg" : "image/png";
            editedUrl = `data:${mimeType};base64,${editedUrl}`;
          }
          
          // Add cache-busting query parameter to force browser to fetch fresh image (only for HTTP URLs)
          let freshUrl = editedUrl;
          if (freshUrl.startsWith("http://") || freshUrl.startsWith("https://")) {
            const separator = freshUrl.includes("?") ? "&" : "?";
            freshUrl = `${freshUrl}${separator}t=${Date.now()}`;
          }
          
          logger.log("✨ Updating preview with edited image:", freshUrl);
          
          // Update display image (fully local - no DB save)
          setDisplayImageUrl(freshUrl);
          
          // Increment edit count (max 3 edits per character)
          const newEditCount = editCount + 1;
          setEditCount(newEditCount);
          
          // Update edit_count in database for existing characters
          if (characterId) {
            supabase
              .from('characters')
              .update({ edit_count: newEditCount })
              .eq('id', characterId)
              .then(({ error }) => {
                if (error) {
                  logger.error("Failed to update edit_count in database:", error);
                } else {
                  logger.log(`✅ Edit count updated to ${newEditCount} in database`);
                }
              });
          }
          
          // Update animatedImageUrl - this will trigger the preview useEffect
          setAnimatedImageUrl(freshUrl);
          
          // Ensure showAnimated is true so the preview shows the edited image
          setShowAnimated(true);
          
          // Don't update uploadedImageUrl - keep the original image for the upload section
          // The preview will use animatedImageUrl when showAnimated is true
          
          setEditPrompt("");
          showToast(`✨ Preview updated! (${newEditCount}/3 edits used)`, "success");
          
          // Force preview update by triggering a small delay to ensure state updates
          setTimeout(() => {
            const canvas = previewCanvasRef.current;
            if (canvas && freshUrl) {
              const ctx = canvas.getContext("2d");
              if (ctx) {
                const img = new Image();
                if (freshUrl.startsWith("http://") || freshUrl.startsWith("https://")) {
                  img.crossOrigin = "anonymous";
                }
                img.onload = () => {
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                  ctx.fillStyle = "#ffffff";
                  ctx.fillRect(0, 0, canvas.width, canvas.height);
                  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                  logger.log("✅ Preview canvas updated with edited image");
                };
                img.onerror = (error) => {
                  logger.error("❌ Failed to load edited image in preview:", error);
                };
                img.src = freshUrl;
              }
            }
          }, 100);
        } else {
          logger.error("❌ No edited image URL found in response:", editResult);
          throw new Error("No edited image received");
        }
      } else {
        throw new Error(editResult.error || "Character editing failed");
      }
    } catch (error) {
      logger.error("Error editing character:", error);
      showToast(`Character editing failed: ${error.message}`, "error");
    } finally {
      setIsProcessingEdit(false);
    }
  };

  const discardEdit = () => {
    setEditPrompt("");
  };


  const handleUpgrade = () => {
    requireParentalVerification('upgrade subscription', () => navigate("/settings?tab=stats"));
  };

  const handleSelectInputMode = (mode) => {
    setInputMode(mode);
    // Reset states when switching modes
    setCharacterName("");
    setAnimatedImageUrl(null);
    setHanddrawnImageUrl(null);
    setShowAnimated(false);
    setEditPrompt("");
    setEditCount(0);
    if (mode === "draw") {
      clearCanvas(true); // Reset history when switching modes
    } else {
      removeUploadedImage();
    }
  };

  if (isCreating && !generatingCharacterId) {
    return <CharacterCreatingPlaceholder size="full" />;
  }


  return (
    <div
      className="character-lab-container min-h-screen"
      style={{
        fontFamily: "'Inter', 'Fredoka', sans-serif",
        background: 'linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)',
      }}
    >
      {/* Soft decorative blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full" style={{ background: 'rgba(0,101,248,0.07)', filter: 'blur(30px)' }}></div>
        <div className="absolute top-40 right-20 w-64 h-64 rounded-full" style={{ background: 'rgba(0,202,255,0.08)', filter: 'blur(30px)' }}></div>
        <div className="absolute bottom-32 left-1/4 w-56 h-56 rounded-full" style={{ background: 'rgba(127,198,255,0.10)', filter: 'blur(30px)' }}></div>
      </div>

      <div className="relative min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
          {/* Character Lab Title - only show on canvas/workspace pages, not method selection */}
          {(characterId || inputMode) && (
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mt-5 mb-8"
              style={{
                fontFamily: "'Fredoka', sans-serif",
                color: '#0B1A33',
                fontWeight: 700,
                fontSize: 'clamp(1.75rem, 3.6vw, 2.5rem)',
                letterSpacing: '-0.01em',
                textTransform: 'uppercase',
              }}
            >
              Character <span style={{ color: '#0065F8' }}>Lab</span>
            </motion.h1>
          )}

          <AnimatePresence mode="wait">
            {characterId ? (
              /* Edit Mode - Existing Character */
              <motion.div
                key="edit-mode"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex flex-col items-center"
              >
                {/* Character Name Display */}
                <motion.h2
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-2xl sm:text-3xl font-bold text-white mb-6 text-center"
                >
                  {characterName || "Your Character"}
                </motion.h2>

                {/* Main Canvas Area */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative w-full max-w-lg"
                >
                  {/* Canvas Container */}
                  <div className="relative bg-white rounded-3xl shadow-2xl border-4 border-purple-400/30 overflow-hidden">
                    <canvas
                      ref={previewCanvasRef}
                      width={300}
                      height={300}
                      className="w-full h-auto"
                      style={{ aspectRatio: "1/1" }}
                    />
                    {isPreviewLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/90">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
                          <p className="text-sm text-gray-600 font-medium">Loading...</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Toggle Original/Animated */}
                  {(handdrawnImageUrl || animatedImageUrl) && (
                    <div className="absolute top-4 right-4 flex gap-1 p-1 bg-black/40 backdrop-blur-md rounded-full">
                      <button
                        type="button"
                        onClick={() => setShowAnimated(false)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                          !showAnimated
                            ? "bg-white text-gray-800"
                            : "text-white/70 hover:text-white"
                        }`}
                      >
                        Original
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAnimated(true)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                          showAnimated
                            ? "bg-white text-gray-800"
                            : "text-white/70 hover:text-white"
                        }`}
                        disabled={!animatedImageUrl}
                      >
                        Animated
                      </button>
                    </div>
                  )}
                </motion.div>

                {/* Edit Panel (Collapsible) */}
                <AnimatePresence>
                  {showEditPanel && animatedImageUrl && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="w-full max-w-lg mt-6 overflow-hidden"
                    >
                      <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/20">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-white">
                            Edit Character
                          </h4>
                          <span className={`text-xs font-medium ${
                            editCount >= 3 ? "text-red-300" : "text-gray-300"
                          }`}>
                            {editCount}/3 edits
                          </span>
                        </div>
                        {editCount >= 3 && (
                          <div className="mb-3 p-2 bg-red-500/20 border border-red-400/30 rounded-lg">
                            <p className="text-xs text-red-200">
                              Maximum edits reached.
                            </p>
                          </div>
                        )}
                        <textarea
                          value={editPrompt}
                          onChange={(e) => setEditPrompt(e.target.value)}
                          rows={2}
                          className="w-full p-3 border border-white/20 rounded-xl text-sm bg-white/10 text-white placeholder-gray-400 focus:border-purple-400 focus:outline-none resize-none mb-3"
                          placeholder="e.g., 'add a hat', 'change shirt to blue'"
                          disabled={isProcessingEdit || editCount >= 3}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={editImagePreview}
                            disabled={isProcessingEdit || !editPrompt.trim() || editCount >= 3}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 disabled:opacity-50 text-white text-sm font-medium rounded-full transition-all disabled:cursor-not-allowed"
                          >
                            {isProcessingEdit ? "Processing..." : "Apply Edit"}
                          </button>
                          <button
                            onClick={() => setShowEditPanel(false)}
                            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-full transition-colors"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Action Buttons */}
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => navigate("/library")}
                    className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white font-medium rounded-full transition-all border border-white/20"
                  >
                    Back to Library
                  </button>
                  {animatedImageUrl && !showEditPanel && (
                    <button
                      onClick={() => setShowEditPanel(true)}
                      disabled={editCount >= 3}
                      className="px-6 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 disabled:opacity-50 text-white font-medium rounded-full transition-all disabled:cursor-not-allowed"
                    >
                      Edit
                    </button>
                  )}
                </div>
              </motion.div>
            ) : generatingCharacterId ? (
              /* Character Generation View — Spinner → Result */
              <motion.div
                key="generating"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-w-md mx-auto min-h-[calc(100vh-200px)] flex flex-col justify-center items-center px-4"
              >
                <div
                  className="w-full aspect-square max-w-[320px] overflow-hidden mb-8 relative"
                  style={{
                    background: '#FFFFFF',
                    border: '2px solid rgba(11,26,51,0.08)',
                    borderRadius: 24,
                    boxShadow: '0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.08)',
                  }}
                >
                  {generatedImageUrl ? (
                    <motion.img
                      initial={{ opacity: 0, scale: 1.1 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5 }}
                      src={generatedImageUrl}
                      alt="Your character"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <CharacterCreatingPlaceholder size="card" />
                  )}
                </div>

                {generatedImageUrl ? (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-[360px] space-y-4">
                    <h2
                      className="text-center mb-1"
                      style={{
                        fontFamily: "'Fredoka', sans-serif",
                        color: '#0B1A33',
                        fontWeight: 700,
                        fontSize: 'clamp(1.5rem, 2.8vw, 2rem)',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      Your character is ready!
                    </h2>
                    <p
                      className="text-center mb-2"
                      style={{ fontFamily: "'Fredoka', sans-serif", color: '#5A6B85', fontSize: 14 }}
                    >
                      Love it? Save it. Or try again for a different look.
                    </p>

                    {/* Two buttons: Try Again + Save */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setGeneratingCharacterId(null);
                          setGeneratedImageUrl(null);
                          setIsCreating(false);
                          setShowNameInput(false);
                          setGeneratingName('');
                          apiClient.deleteCharacter && apiClient.deleteCharacter(generatingCharacterId).catch(() => {});
                        }}
                        className="flex-1 py-3.5 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                        style={{
                          fontFamily: "'Fredoka', sans-serif",
                          background: '#FFFFFF',
                          color: '#0065F8',
                          fontWeight: 700,
                          fontSize: 15,
                          border: '2px solid rgba(0,101,248,0.22)',
                          borderRadius: 999,
                          boxShadow: '0px 3px 0px rgba(11,26,51,0.06)',
                          cursor: 'pointer',
                        }}
                      >
                        <span className="material-symbols-outlined text-lg" style={{ color: '#0065F8' }}>refresh</span> Try Again
                      </button>
                      <button
                        onClick={() => setShowNameInput(true)}
                        className="flex-1 py-3.5 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                        style={{
                          fontFamily: "'Fredoka', sans-serif",
                          background: '#0065F8',
                          color: '#FFFFFF',
                          fontWeight: 700,
                          fontSize: 15,
                          border: 'none',
                          borderRadius: 999,
                          boxShadow: '0px 4px 0px #073B8E, 0px 12px 28px rgba(0,101,248,0.18)',
                          cursor: 'pointer',
                        }}
                      >
                        <span className="material-symbols-outlined text-lg" style={{ fontVariationSettings: "'FILL' 1", color: '#FFFFFF' }}>download_done</span> Save
                      </button>
                    </div>

                    {/* Name Modal */}
                    <AnimatePresence>
                      {showNameInput && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="space-y-3 pt-2"
                        >
                          <input
                            type="text"
                            value={generatingName}
                            onChange={(e) => setGeneratingName(e.target.value)}
                            className="w-full p-4 text-lg font-medium focus:outline-none transition-all text-center"
                            style={{
                              fontFamily: "'Fredoka', sans-serif",
                              background: '#FFFFFF',
                              color: '#0B1A33',
                              border: '2px solid rgba(11,26,51,0.08)',
                              borderRadius: 18,
                              boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
                            }}
                            placeholder="Name your character..."
                            autoFocus
                          />
                          <button
                            onClick={async () => {
                              const name = generatingName.trim();
                              if (!name) { showToast('Please name your character', 'error'); return; }

                              if (!requireEntitlement({ trigger: "creation", contentType: "character" })) {
                                return;
                              }

                              const starCost = costs?.create_character ?? 25;
                              const currentStars = user?.pixley_stars ?? 0;

                              // Gate by balance before deducting
                              if (starCost > 0 && currentStars < starCost) {
                                setPaywallStarCost(starCost);
                                setPaywallCurrentStars(currentStars);
                                setShowPaywallModal(true);
                                return;
                              }

                              try {
                                // Deduct stars first
                                if (starCost > 0) {
                                  const useStarsResp = await apiClient.useStars(starCost, "create_character");
                                  if (useStarsResp.status && useStarsResp.data?.pixley_stars !== undefined) {
                                    updateUser({ ...user, pixley_stars: useStarsResp.data.pixley_stars });
                                  } else {
                                    showToast(useStarsResp.error || 'Failed to deduct stars', 'error');
                                    return;
                                  }
                                }

                                // Persist the chosen name on the character row
                                await apiClient.updateCharacter(generatingCharacterId, { name });

                                setSavedCharacterData({
                                  id: generatingCharacterId,
                                  name: name,
                                  imageUrl: generatedImageUrl,
                                });
                                setShowSuccessScreen(true);
                                setGeneratingCharacterId(null);
                                setGeneratedImageUrl(null);
                                setShowNameInput(false);
                                setGeneratingName('');
                              } catch (e) {
                                logger.error('Save & Continue failed:', e);
                                showToast('Failed to save character', 'error');
                              }
                            }}
                            className="w-full py-4 inline-flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                            style={{
                              fontFamily: "'Fredoka', sans-serif",
                              background: '#0065F8',
                              color: '#FFFFFF',
                              fontWeight: 700,
                              fontSize: 17,
                              border: 'none',
                              borderRadius: 999,
                              boxShadow: '0px 4px 0px #073B8E, 0px 12px 28px rgba(0,101,248,0.18)',
                              cursor: 'pointer',
                            }}
                          >
                            Save &amp; Continue
                            {(costs?.create_character ?? 25) > 0 && (
                              <span
                                className="inline-flex items-center gap-1 ml-1"
                                style={{
                                  background: 'rgba(255,255,255,0.22)',
                                  borderRadius: 999,
                                  padding: '3px 10px',
                                  fontSize: 13,
                                  fontWeight: 800,
                                }}
                              >
                                {costs?.create_character ?? 25}
                                <img src="/assets/pixley_stars.png" alt="" style={{ width: 13, height: 13, filter: "brightness(0) invert(1)" }} />
                              </span>
                            )}
                            <span style={{ marginLeft: 2 }}>→</span>
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ) : (
                  <p style={{ fontFamily: "'Fredoka', sans-serif", color: '#5A6B85', fontSize: 13 }}>
                    This usually takes 15-30 seconds
                  </p>
                )}
              </motion.div>
            ) : !inputMode ? (
              /* Prompt-First Character Creation — Centered */
              <motion.div
                key="prompt"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="max-w-2xl mx-auto min-h-[calc(100vh-200px)] flex flex-col justify-center px-4"
              >
                {/* Title */}
                <motion.h1
                  initial={{ opacity: 0, y: -15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                  className="text-center mb-3"
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    color: '#0B1A33',
                    fontWeight: 700,
                    fontSize: 'clamp(2rem, 4.5vw, 3.4rem)',
                    letterSpacing: '-0.02em',
                    textTransform: 'uppercase',
                    lineHeight: 1.0,
                  }}
                >
                  Create Your <span style={{ color: '#0065F8' }}>Character</span>
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
                  className="text-center mb-10 max-w-md mx-auto"
                  style={{ fontFamily: "'Fredoka', sans-serif", color: '#5A6B85', fontSize: 15, lineHeight: 1.5 }}
                >
                  Describe your character and our AI will bring it to life.
                </motion.p>

                {/* Prompt + Upload Area */}
                <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                  <div
                    className="relative transition-all duration-200"
                    style={{
                      background: '#FFFFFF',
                      border: `2px solid ${isDragOver ? '#0065F8' : 'rgba(11,26,51,0.08)'}`,
                      borderRadius: 22,
                      boxShadow: isDragOver
                        ? '0px 4px 0px rgba(0,101,248,0.18), 0px 12px 28px rgba(0,101,248,0.10)'
                        : '0px 2px 0px rgba(11,26,51,0.04), 0px 8px 20px rgba(11,26,51,0.06)',
                    }}
                    onDrop={(e) => { e.preventDefault(); setIsDragOver(false); const f = e.dataTransfer.files[0]; if (f?.type.startsWith('image/')) { setPromptImage(f); const r = new FileReader(); r.onload = (ev) => setPromptImagePreview(ev.target.result); r.readAsDataURL(f); } }}
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={() => setIsDragOver(false)}
                  >
                    <textarea
                      value={promptText}
                      onChange={(e) => setPromptText(e.target.value)}
                      className="w-full min-h-[180px] sm:min-h-[220px] p-6 pb-16 bg-transparent focus:outline-none resize-none"
                      style={{
                        fontFamily: "'Fredoka', sans-serif",
                        color: '#0B1A33',
                        fontSize: 16,
                        lineHeight: 1.5,
                      }}
                      placeholder="A friendly blue dragon with golden wings who loves to bake cookies..."
                    />

                    {/* Uploaded image chip */}
                    {promptImagePreview && (
                      <div className="px-6 pb-4">
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                          className="inline-flex items-center gap-3 p-3 pr-5"
                          style={{
                            background: 'rgba(0,101,248,0.06)',
                            border: '2px solid rgba(0,101,248,0.18)',
                            borderRadius: 16,
                          }}
                        >
                          <img src={promptImagePreview} alt="Upload" className="w-16 h-16 object-cover" style={{ borderRadius: 12, boxShadow: '0px 2px 0px rgba(11,26,51,0.06)' }} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate" style={{ fontFamily: "'Fredoka', sans-serif", color: '#0B1A33', fontWeight: 600 }}>{promptImage?.name}</p>
                            <p className="text-xs" style={{ fontFamily: "'Fredoka', sans-serif", color: '#5A6B85' }}>{(promptImage?.size / 1024).toFixed(0)} KB</p>
                          </div>
                          <button onClick={() => { setPromptImage(null); setPromptImagePreview(null); }} className="p-1.5 transition-colors rounded-lg" style={{ color: '#5A6B85' }}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </motion.div>
                      </div>
                    )}

                    {/* Bottom bar */}
                    <div className="absolute bottom-4 left-5 right-5 flex items-center gap-2">
                      <button
                        onClick={() => promptFileInputRef.current?.click()}
                        className="flex items-center gap-2 px-3.5 py-2 transition-all text-sm"
                        style={{
                          fontFamily: "'Fredoka', sans-serif",
                          background: '#FFFFFF',
                          color: '#334155',
                          fontWeight: 600,
                          border: '2px solid rgba(11,26,51,0.08)',
                          borderRadius: 999,
                          boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
                        }}
                      >
                        <span className="material-symbols-outlined text-[16px]" style={{ color: '#0065F8' }}>add_photo_alternate</span>
                        Image
                      </button>
                      <button
                        onClick={generateRandomPrompt}
                        disabled={isShuffling}
                        className="flex items-center gap-2 px-3.5 py-2 transition-all text-sm disabled:opacity-50"
                        style={{
                          fontFamily: "'Fredoka', sans-serif",
                          background: '#FFFFFF',
                          color: '#334155',
                          fontWeight: 600,
                          border: '2px solid rgba(11,26,51,0.08)',
                          borderRadius: 999,
                          boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
                        }}
                      >
                        <span className={`material-symbols-outlined text-[16px] ${isShuffling ? 'animate-spin' : ''}`} style={{ color: '#0065F8' }}>casino</span>
                        {isShuffling ? 'Thinking...' : 'Surprise me'}
                      </button>
                    </div>
                    <input ref={promptFileInputRef} type="file" accept="image/*" className="hidden"
                      onChange={(e) => { const f = e.target.files[0]; if (f?.type.startsWith('image/')) { setPromptImage(f); const r = new FileReader(); r.onload = (ev) => setPromptImagePreview(ev.target.result); r.readAsDataURL(f); setAutoCreateFromImage(f); } }} />
                  </div>
                </motion.div>

                {/* Create Button */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8">
                  <button
                    onClick={async () => {
                      if (!promptText.trim() && !promptImage) { showToast('Please describe your character or upload an image', 'error'); return; }
                      if (!requireEntitlement({ trigger: "creation", contentType: "character" })) { saveLabDraft(); return; }
                      trackCharacterCreationStarted({ method: promptImage ? 'image' : 'text', name: (characterName.trim() || promptText.trim().slice(0, 20) || "Character"), hasImage: !!promptImage });
                      try {
                        setIsCreating(true);
                        const compressedPromptImage = promptImage ? await compressImage(promptImage, { outputFileName: promptImage.name }) : undefined;
                        const response = await apiClient.previewCharacterFromPrompt({
                          user_id: user?.id,
                          image: compressedPromptImage,
                          handdrawn: false,
                          prompt: promptText.trim() || undefined,
                        });
                        applyPreviewResponse(response);
                        showToast('Preview generated. Click Save to create your character.', 'success');
                      } catch (error) {
                        showToast(error.message || 'Character preview failed', 'error');
                      } finally {
                        setIsCreating(false);
                      }
                    }}
                    disabled={isCreating}
                    data-create-prompt-btn
                    className="group w-full py-4 transition-all duration-150 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed relative"
                    style={{
                      fontFamily: "'Fredoka', sans-serif",
                      background: '#0065F8',
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: 17,
                      border: 'none',
                      borderRadius: 999,
                      boxShadow: '0px 4px 0px #073B8E, 0px 12px 28px rgba(0,101,248,0.18)',
                      cursor: 'pointer',
                    }}
                  >
                    {isCreating ? (
                      <span className="inline-flex items-center gap-3">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Creating your character...
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        Create Character
                        <motion.span animate={{ x: [0, 4, 0] }} transition={{ duration: 1.5, repeat: Infinity }} className="text-xl">→</motion.span>
                      </span>
                    )}
                  </button>
                </motion.div>

                {/* Secondary options */}
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                  className="mt-8 flex justify-center gap-3 flex-wrap">
                  {[
                    { id: 'random', icon: 'auto_awesome', label: isShuffling ? 'Generating...' : 'Random Character', primary: true, onClick: async () => {
                      if (!requireEntitlement({ trigger: "creation", contentType: "character" })) { saveLabDraft(); return; }
                      setIsShuffling(true);
                      try {
                        const resp = await apiClient.request('/generate-character-prompt');
                        const prompt = resp?.data?.prompt || resp?.prompt;
                        if (!prompt) {
                          showToast('Failed to generate prompt', 'error');
                          return;
                        }
                        setPromptText(prompt);
                        setPromptImage(null);
                        setPromptImagePreview(null);
                        setIsCreating(true);
                        const previewResp = await apiClient.previewCharacterFromPrompt({
                          user_id: user?.id,
                          prompt,
                          handdrawn: false,
                        });
                        applyPreviewResponse(previewResp);
                        showToast('Preview generated. Click Save to create your character.', 'success');
                      } catch (e) {
                        showToast(e.message || 'Character preview failed', 'error');
                      } finally {
                        setIsCreating(false);
                        setIsShuffling(false);
                      }
                    }},
                    { id: 'camera', icon: 'photo_camera', label: 'Camera', onClick: () => setShowWebcam(true) },
                    { id: 'draw', icon: 'brush', label: 'Draw', onClick: () => handleSelectInputMode('draw') },
                    { id: 'upload', icon: 'cloud_upload', label: 'Upload', onClick: () => uploadBtnRef.current?.click() },
                  ].map((opt) => (
                    <motion.button
                      key={opt.id}
                      onClick={opt.onClick}
                      disabled={opt.id === 'random' && isShuffling}
                      whileTap={{ scale: 0.97 }}
                      className="flex items-center gap-2 px-5 py-2.5 transition-all duration-150 disabled:opacity-60"
                      style={{
                        fontFamily: "'Fredoka', sans-serif",
                        background: opt.primary ? '#FFFFFF' : '#FFFFFF',
                        color: opt.primary ? '#0065F8' : '#334155',
                        fontWeight: 700,
                        fontSize: 14,
                        border: `2px solid ${opt.primary ? 'rgba(0,101,248,0.22)' : 'rgba(11,26,51,0.08)'}`,
                        borderRadius: 999,
                        boxShadow: '0px 3px 0px rgba(11,26,51,0.06)',
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        className={`material-symbols-outlined text-lg ${opt.id === 'random' && isShuffling ? 'animate-spin' : ''}`}
                        style={{ color: opt.primary ? '#0065F8' : '#5A6B85', fontVariationSettings: opt.primary ? "'FILL' 1" : undefined }}
                      >
                        {opt.icon}
                      </span>
                      {opt.label}
                    </motion.button>
                  ))}
                  <input ref={uploadBtnRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files[0]; if (f?.type.startsWith('image/')) { setAutoCreateFromImage(f); } e.target.value = ''; }} />
                </motion.div>

              </motion.div>
            ) : (
              /* Main Canvas Workspace */
              <motion.div
                key="workspace"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex flex-col items-center"
              >
                {/* Main Canvas Area */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative w-full max-w-md mb-6 mx-auto"
                >
                  {/* Canvas Container */}
                  <div
                    className="relative overflow-hidden select-none"
                    style={{
                      background: '#FFFFFF',
                      border: '2px solid rgba(0,101,248,0.18)',
                      borderRadius: 24,
                      boxShadow: '0px 6px 0px rgba(11,26,51,0.06), 0px 18px 40px rgba(11,26,51,0.10)',
                      touchAction: inputMode === 'draw' && !animatedImageUrl ? 'none' : 'auto',
                      WebkitUserSelect: 'none',
                      WebkitTouchCallout: 'none',
                    }}
                  >
                    {/* Show generated character OR drawing canvas OR uploaded image OR empty state */}
                    {(showPreview && previewImage) || animatedImageUrl ? (
                      /* Generated Character Display - switches between original and animated */
                      <div className="relative">
                        <img
                          src={
                            showAnimated
                              ? (animatedImageUrl || previewImage)
                              : (inputMode === "draw" ? handdrawnImageUrl : uploadedImageUrl) || animatedImageUrl || previewImage
                          }
                          alt={showAnimated ? "Generated character" : "Original drawing"}
                          className="w-full h-auto"
                          style={{ aspectRatio: "1/1", objectFit: "cover" }}
                        />
                      </div>
                    ) : inputMode === "draw" ? (
                      /* Drawing Canvas */
                      <>
                        <canvas
                          ref={canvasRef}
                          width={500}
                          height={500}
                          className="w-full h-auto cursor-crosshair select-none"
                          style={{ aspectRatio: "1/1", touchAction: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none', userSelect: 'none' }}
                          onMouseDown={startDrawing}
                          onMouseMove={draw}
                          onMouseUp={stopDrawing}
                          onMouseLeave={stopDrawing}
                          onTouchStart={startDrawing}
                          onTouchMove={draw}
                          onTouchEnd={stopDrawing}
                        />
                        {/* Placeholder text when canvas is empty */}
                        {!hasDrawing() && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none select-none">
                            <p
                              className="mb-2 select-none"
                              style={{
                                fontFamily: "'Fredoka', sans-serif",
                                color: '#0065F8',
                                fontWeight: 700,
                                fontSize: 24,
                                letterSpacing: '-0.01em',
                              }}
                            >
                              Draw your friend!
                            </p>
                            <p
                              className="select-none"
                              style={{
                                fontFamily: "'Fredoka', sans-serif",
                                color: '#5A6B85',
                                fontSize: 14,
                              }}
                            >
                              Use the tools below to start creating
                            </p>
                          </div>
                        )}
                      </>
                    ) : inputMode === "upload" && uploadedImageUrl ? (
                      /* Uploaded Image Display */
                      <img
                        src={uploadedImageUrl}
                        alt="Uploaded character"
                        className="w-full h-auto"
                        style={{ aspectRatio: "1/1", objectFit: "cover" }}
                      />
                    ) : (
                      /* Empty Upload State */
                      <div
                        className="w-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
                        style={{ aspectRatio: "1/1" }}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <div className="w-16 h-16 mb-4 bg-emerald-100 rounded-2xl flex items-center justify-center">
                          <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                        </div>
                        <p className="text-lg font-bold text-gray-600 mb-1">Click to upload</p>
                        <p className="text-sm text-gray-400">JPG, PNG, GIF</p>
                      </div>
                    )}

                    {/* Processing Animation Overlay */}
                    {isProcessingAnimation && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/90">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
                          <p className="text-base font-medium text-gray-700">Creating magic...</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </motion.div>

                {/* Edit Panel (Collapsible) - Only after generation */}
                <AnimatePresence>
                  {showEditPanel && animatedImageUrl && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="w-full max-w-lg mb-6 overflow-hidden"
                    >
                      <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/20">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-white">Edit Character</h4>
                          <span className={`text-xs font-medium ${editCount >= 3 ? "text-red-300" : "text-gray-300"}`}>
                            {editCount}/3 edits
                          </span>
                        </div>
                        {editCount >= 3 && (
                          <div className="mb-3 p-2 bg-red-500/20 border border-red-400/30 rounded-lg">
                            <p className="text-xs text-red-200">Maximum edits reached.</p>
                          </div>
                        )}
                        <textarea
                          value={editPrompt}
                          onChange={(e) => setEditPrompt(e.target.value)}
                          rows={2}
                          className="w-full p-3 border border-white/20 rounded-xl text-sm bg-white/10 text-white placeholder-gray-400 focus:border-purple-400 focus:outline-none resize-none mb-3"
                          placeholder="e.g., 'add a hat', 'change shirt to blue'"
                          disabled={isProcessingEdit || editCount >= 3}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={editImagePreview}
                            disabled={isProcessingEdit || !editPrompt.trim() || editCount >= 3}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 disabled:opacity-50 text-white text-sm font-medium rounded-full transition-all disabled:cursor-not-allowed"
                          >
                            {isProcessingEdit ? "Processing..." : "Apply Edit"}
                          </button>
                          <button
                            onClick={() => setShowEditPanel(false)}
                            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-full transition-colors"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Bottom Toolbar */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className={`w-full ${animatedImageUrl ? 'max-w-md' : 'max-w-3xl'}`}
                >
                  <div
                    className={animatedImageUrl ? 'p-2 sm:p-2.5' : 'p-2.5 sm:p-4'}
                    style={{
                      background: '#FFFFFF',
                      border: '2px solid rgba(11,26,51,0.06)',
                      borderRadius: animatedImageUrl ? 999 : 999,
                      boxShadow: '0px 2px 0px rgba(11,26,51,0.04), 0px 8px 20px rgba(11,26,51,0.06)',
                    }}
                  >
                    {/* Pre-generation: stacked on mobile, single row on desktop */}
                    {!animatedImageUrl && (
                      <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
                        {/* Tools row */}
                        <div className="flex items-center justify-center gap-1.5 sm:gap-3 flex-1">
                          {inputMode === "draw" && (
                            <>
                              {/* Mode Switcher */}
                              <div
                                className="flex items-center p-0.5 sm:p-1"
                                style={{ background: 'rgba(11,26,51,0.04)', borderRadius: 999 }}
                              >
                                <div
                                  className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white rounded-full"
                                  style={{ background: '#0065F8', boxShadow: '0px 2px 0px #073B8E' }}
                                >
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M7 14c-1.66 0-3 1.34-3 3 0 1.31.84 2.41 2 2.83V22h2v-2.17c1.16-.41 2-1.51 2-2.83 0-1.66-1.34-3-3-3zM20.71 4.63l-1.34-1.34c-.39-.39-1.02-.39-1.41 0L9 12.25 11.75 15l8.96-8.96c.39-.39.39-1.02 0-1.41z" />
                                  </svg>
                                </div>
                                <button
                                  onClick={() => handleSelectInputMode("upload")}
                                  className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full transition-all"
                                  style={{ color: '#5A6B85' }}
                                  title="Switch to Upload"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                </button>
                              </div>

                              {/* Color Picker */}
                              <label className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center cursor-pointer hover:scale-110 transition-all" title="Color">
                                <input
                                  type="color"
                                  value={brushColor}
                                  onChange={(e) => setBrushColor(e.target.value)}
                                  className="opacity-0 absolute w-0 h-0"
                                />
                                <div
                                  className="w-5 h-5 sm:w-6 sm:h-6 rounded-full"
                                  style={{ backgroundColor: brushColor, border: '2px solid rgba(11,26,51,0.15)' }}
                                />
                              </label>

                              {/* Brush Size */}
                              <div
                                className="flex items-center gap-1.5 sm:gap-2 px-2 py-1 sm:px-3 sm:py-1.5"
                                style={{ background: 'rgba(11,26,51,0.04)', borderRadius: 999 }}
                              >
                                <div
                                  className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full"
                                  style={{ background: '#5A6B85', transform: `scale(${0.5 + (brushSize / 20) * 0.8})` }}
                                />
                                <input
                                  type="range"
                                  min="1"
                                  max="20"
                                  value={brushSize}
                                  onChange={(e) => setBrushSize(e.target.value)}
                                  className="w-14 sm:w-24"
                                  style={{ accentColor: '#0065F8' }}
                                />
                                <span className="text-[10px] sm:text-xs font-medium" style={{ color: '#5A6B85' }}>{brushSize}px</span>
                              </div>

                              {/* Undo/Redo */}
                              <div
                                className="flex items-center p-0.5 sm:p-1"
                                style={{ background: 'rgba(11,26,51,0.04)', borderRadius: 999 }}
                              >
                                <button
                                  onClick={undoDrawing}
                                  disabled={drawingHistory.length <= 1}
                                  className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                  style={{ color: '#5A6B85' }}
                                  title="Undo"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                  </svg>
                                </button>
                                <button
                                  onClick={redoDrawing}
                                  disabled={redoHistory.length === 0}
                                  className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                  style={{ color: '#5A6B85' }}
                                  title="Redo"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
                                  </svg>
                                </button>
                              </div>

                              {/* Clear */}
                              <button
                                onClick={() => clearCanvas()}
                                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full transition-all"
                                style={{ background: 'rgba(11,26,51,0.04)', color: '#5A6B85' }}
                                title="Clear"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}

                          {inputMode === "upload" && (
                            <>
                              {/* Mode Switcher */}
                              <div className="flex items-center bg-white/5 rounded-full p-1 sm:p-1.5">
                                <button
                                  onClick={() => handleSelectInputMode("draw")}
                                  className="w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-all"
                                  title="Switch to Draw"
                                >
                                  <svg className="w-4.5 h-4.5 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M7 14c-1.66 0-3 1.34-3 3 0 1.31.84 2.41 2 2.83V22h2v-2.17c1.16-.41 2-1.51 2-2.83 0-1.66-1.34-3-3-3zM20.71 4.63l-1.34-1.34c-.39-.39-1.02-.39-1.41 0L9 12.25 11.75 15l8.96-8.96c.39-.39.39-1.02 0-1.41z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => fileInputRef.current?.click()}
                                  className="w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center bg-emerald-500/60 text-white rounded-full transition-all"
                                  title="Upload Image"
                                >
                                  <svg className="w-4.5 h-4.5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                </button>
                              </div>

                              {/* Clear */}
                              <button
                                onClick={() => removeUploadedImage()}
                                className="w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center text-white/50 hover:text-red-400 bg-white/5 hover:bg-white/10 rounded-full transition-all"
                                title="Remove Image"
                              >
                                <svg className="w-4.5 h-4.5 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>

                        {/* Create Button - full width row on mobile, inline on desktop */}
                        <button
                          onClick={animateImage}
                          disabled={
                            isProcessingAnimation ||
                            (inputMode === "draw" && !hasDrawing()) ||
                            (inputMode === "upload" && !uploadedImage)
                          }
                          className="w-full sm:w-auto px-6 py-2.5 sm:px-8 sm:py-3 disabled:opacity-50 text-sm sm:text-lg transition-all disabled:cursor-not-allowed flex-shrink-0"
                          style={{
                            fontFamily: "'Fredoka', sans-serif",
                            background: '#0065F8',
                            color: '#FFFFFF',
                            fontWeight: 700,
                            border: 'none',
                            borderRadius: 999,
                            boxShadow: '0px 4px 0px #073B8E, 0px 12px 28px rgba(0,101,248,0.18)',
                            cursor: 'pointer',
                          }}
                        >
                          Create
                        </button>
                      </div>
                    )}

                    {/* Post-generation controls */}
                    {animatedImageUrl && (
                      <div className="flex items-center justify-center gap-3">
                        <button
                          onClick={async () => {
                            // Discard the current preview and return to the main
                            // "Create Your Character" page (no DB row exists yet).
                            setPreviewId(null);
                            setShowPreview(false);
                            setPreviewImage(null);
                            setAnimatedImageUrl(null);
                            setHanddrawnImageUrl(null);
                            setGeneratedImageUrl(null);
                            setShowAnimated(false);
                            setEditCount(0);
                            setShowEditPanel(false);
                            setRedoHistory([]);
                            if (inputMode === "draw") {
                              clearCanvas(true);
                            }
                            // Clear any uploaded/prompt image so the default
                            // page renders clean, then drop back to it.
                            removeUploadedImage();
                            setPromptImage(null);
                            setPromptImagePreview(null);
                            setInputMode(null);
                          }}
                          className="transition active:scale-[0.97]"
                          style={{
                            fontFamily: "'Fredoka', sans-serif",
                            background: "#FFFFFF",
                            color: "#5A6B85",
                            fontWeight: 700,
                            fontSize: 13,
                            border: "2px solid rgba(11,26,51,0.10)",
                            borderRadius: 999,
                            padding: "9px 16px",
                            boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                            cursor: "pointer",
                          }}
                        >
                          Try Again
                        </button>
                        <button
                          onClick={() => setShowSaveModal(true)}
                          className="inline-flex items-center gap-1.5 transition active:scale-[0.97]"
                          style={{
                            fontFamily: "'Fredoka', sans-serif",
                            background: "#0065F8",
                            color: "#FFFFFF",
                            fontWeight: 700,
                            fontSize: 13,
                            border: "none",
                            borderRadius: 999,
                            padding: "9px 18px",
                            boxShadow: "0px 3px 0px #073B8E, 0px 10px 22px rgba(0,101,248,0.20)",
                            cursor: "pointer",
                          }}
                        >
                          Save
                          {costs?.create_character > 0 && (
                            <span
                              className="inline-flex items-center gap-1"
                              style={{
                                background: "rgba(255,255,255,0.22)",
                                borderRadius: 999,
                                padding: "2px 7px",
                                fontSize: 11,
                                fontWeight: 800,
                              }}
                            >
                              {costs.create_character}
                              <img src="/assets/pixley_stars.png" alt="" style={{ width: 11, height: 11, filter: "brightness(0) invert(1)" }} />
                            </span>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Name + Save Modal — opens when user clicks Save on the preview screen */}
      <AnimatePresence>
        {showSaveModal && (() => {
          const FREDOKA_FF = "'Fredoka', sans-serif";
          // Fall back to 25 if costs hasn't loaded yet (don't collapse to 0 → hides the cost notice).
          const starCost = costs?.create_character ?? 25;
          return (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{ background: "rgba(11,26,51,0.42)", backdropFilter: "blur(8px)" }}
              onClick={() => { if (!isCreating) setShowSaveModal(false); }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-md p-6"
                style={{
                  maxHeight: "calc(100vh - 32px)",
                  overflowY: "auto",
                  background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
                  border: "2px solid rgba(11,26,51,0.06)",
                  borderRadius: 24,
                  boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
                  fontFamily: "'Inter', 'Fredoka', sans-serif",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="text-center" style={{ fontFamily: FREDOKA_FF, color: "#0B1A33", fontWeight: 700, fontSize: 22, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "0 0 16px" }}>
                  Name your character
                </h2>

                {animatedImageUrl && (
                  <div className="flex justify-center mb-4">
                    <div
                      style={{
                        width: 96,
                        height: 96,
                        borderRadius: 20,
                        overflow: "hidden",
                        background: "#FFFFFF",
                        border: "3px solid #FFFFFF",
                        boxShadow: "0px 3px 0px rgba(11,26,51,0.08), 0px 0px 0px 3px rgba(0,101,248,0.15)",
                      }}
                    >
                      <img src={animatedImageUrl} alt="" className="w-full h-full object-cover" />
                    </div>
                  </div>
                )}

                <input
                  type="text"
                  value={characterName}
                  onChange={(e) => setCharacterName(e.target.value)}
                  style={{
                    fontFamily: FREDOKA_FF,
                    width: "100%",
                    background: "#FFFFFF",
                    color: "#0B1A33",
                    fontSize: 15,
                    fontWeight: 600,
                    border: "2px solid rgba(11,26,51,0.10)",
                    borderRadius: 14,
                    padding: "12px 16px",
                    outline: "none",
                    transition: "border-color 200ms",
                    marginBottom: 16,
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#0065F8")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(11,26,51,0.10)")}
                  placeholder="Enter character name…"
                  autoFocus
                  maxLength={50}
                />

                <div style={{ marginBottom: 16 }}>
                  <VoicePicker
                    value={voiceChoice}
                    onChange={setVoiceChoice}
                    onRecord={() => setVoiceRecorderOpen(true)}
                    imageUrl={animatedImageUrl}
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => { if (!isCreating) setShowSaveModal(false); }}
                    disabled={isCreating}
                    className="flex-1 transition active:scale-[0.98] disabled:opacity-50"
                    style={{
                      fontFamily: FREDOKA_FF,
                      background: "#FFFFFF",
                      color: "#5A6B85",
                      fontWeight: 700,
                      fontSize: 14,
                      border: "2px solid rgba(11,26,51,0.10)",
                      borderRadius: 999,
                      padding: "12px 18px",
                      boxShadow: "0px 2px 0px rgba(11,26,51,0.05)",
                      cursor: isCreating ? "not-allowed" : "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={(e) => {
                      handleSubmit(e);
                    }}
                    disabled={!previewId || isCreating}
                    className="flex-1 inline-flex items-center justify-center gap-2 transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      fontFamily: FREDOKA_FF,
                      background: "#0065F8",
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: 14,
                      border: "none",
                      borderRadius: 999,
                      padding: "12px 18px",
                      boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
                      cursor: "pointer",
                    }}
                  >
                    {isCreating ? (
                      <>
                        <span style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.30)", borderTopColor: "#FFFFFF", animation: "char-save-spin 1s linear infinite" }} />
                        Saving…
                      </>
                    ) : (
                      <>
                        Save
                        {starCost > 0 && (
                          <span
                            className="inline-flex items-center gap-1"
                            style={{
                              background: "rgba(255,255,255,0.22)",
                              borderRadius: 999,
                              padding: "2px 8px",
                              fontSize: 12,
                              fontWeight: 800,
                            }}
                          >
                            {starCost}
                            <img src="/assets/pixley_stars.png" alt="" style={{ width: 12, height: 12, filter: "brightness(0) invert(1)" }} />
                          </span>
                        )}
                      </>
                    )}
                  </button>
                </div>

                <style>{`@keyframes char-save-spin { to { transform: rotate(360deg); } }`}</style>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>
      

      <VoiceRecorder
        isOpen={voiceRecorderOpen}
        onClose={() => setVoiceRecorderOpen(false)}
        onDone={(file) => { setVoiceRecorderOpen(false); setVoiceChoice({ type: 'recorded', file }); }}
      />

      {/* Paywall Modal */}
      <PaywallModal
        isOpen={showPaywallModal}
        onClose={() => setShowPaywallModal(false)}
        starCost={paywallStarCost}
        currentStars={paywallCurrentStars}
        contentType="character"
        apiClient={apiClient}
        user={user}
      />

      {/* Webcam Modal */}
      <AnimatePresence>
        {showWebcam && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowWebcam(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#1a1a2e] rounded-3xl overflow-hidden max-w-md w-full border border-white/10 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 flex items-center justify-between border-b border-white/10">
                <h3 className="text-white font-bold text-lg">Take a Photo</h3>
                <button onClick={() => setShowWebcam(false)} className="text-white/40 hover:text-white p-1 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="relative aspect-square bg-black">
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/png"
                  videoConstraints={{ facingMode: "user", width: 720, height: 720 }}
                  className="w-full h-full object-cover"
                  mirrored
                />
              </div>
              <div className="p-4 flex justify-center">
                <button
                  onClick={() => {
                    const screenshot = webcamRef.current?.getScreenshot();
                    if (screenshot) {
                      // Convert data URL to File and auto-create character
                      fetch(screenshot)
                        .then(res => res.blob())
                        .then(blob => {
                          const file = new File([blob], 'webcam-photo.png', { type: 'image/png' });
                          setPromptImage(file);
                          setPromptImagePreview(screenshot);
                          setShowWebcam(false);
                          autoCreateMethodRef.current = 'camera';
                          setAutoCreateFromImage(file);
                        });
                    }
                  }}
                  className="w-16 h-16 bg-white rounded-full border-4 border-white/30 hover:scale-110 active:scale-95 transition-transform shadow-lg"
                >
                  <div className="w-full h-full rounded-full bg-white hover:bg-gray-100 transition-colors" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Character Created Success Screen */}
      <AnimatePresence>
        {showSuccessScreen && savedCharacterData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
            style={{
              fontFamily: "'Inter', 'Fredoka', sans-serif",
              background: 'linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)',
            }}
          >
            {/* Soft brand blobs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <motion.div
                animate={{ scale: [1, 1.12, 1] }}
                transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  position: 'absolute', top: -160, left: -160, width: 480, height: 480,
                  borderRadius: '50%', background: 'rgba(0,101,248,0.10)', filter: 'blur(30px)',
                }}
              />
              <motion.div
                animate={{ scale: [1, 1.18, 1] }}
                transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  position: 'absolute', bottom: -200, right: -200, width: 520, height: 520,
                  borderRadius: '50%', background: 'rgba(0,202,255,0.12)', filter: 'blur(30px)',
                }}
              />
            </div>

            {/* Falling confetti */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[
                { left: '8%',  color: '#0065F8', size: 10, delay: 0.0, dur: 4.0 },
                { left: '17%', color: '#FFD76A', size: 8,  delay: 0.6, dur: 4.6 },
                { left: '26%', color: '#FF6B9A', size: 12, delay: 0.2, dur: 5.2 },
                { left: '36%', color: '#00CAFF', size: 9,  delay: 1.1, dur: 4.4 },
                { left: '46%', color: '#0065F8', size: 7,  delay: 0.4, dur: 5.0 },
                { left: '56%', color: '#FFD76A', size: 11, delay: 0.9, dur: 4.2 },
                { left: '66%', color: '#FF6B9A', size: 8,  delay: 0.0, dur: 4.8 },
                { left: '76%', color: '#00CAFF', size: 10, delay: 0.7, dur: 5.4 },
                { left: '86%', color: '#0065F8', size: 9,  delay: 0.3, dur: 4.6 },
                { left: '94%', color: '#FFD76A', size: 7,  delay: 1.2, dur: 5.0 },
              ].map((c, i) => (
                <motion.div
                  key={i}
                  initial={{ y: -40, opacity: 0, rotate: 0 }}
                  animate={{ y: '110vh', opacity: [0, 1, 1, 0], rotate: 360 }}
                  transition={{
                    delay: c.delay,
                    duration: c.dur,
                    repeat: Infinity,
                    ease: 'linear',
                    times: [0, 0.08, 0.9, 1],
                  }}
                  style={{
                    position: 'absolute',
                    left: c.left,
                    top: 0,
                    width: c.size,
                    height: c.size,
                    borderRadius: i % 2 === 0 ? '50%' : 3,
                    background: c.color,
                    boxShadow: `0 2px 6px ${c.color}55`,
                  }}
                />
              ))}
            </div>

            {/* Content */}
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 24 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ delay: 0.15, type: 'spring', stiffness: 220, damping: 22 }}
              className="relative z-10 flex flex-col items-center text-center px-6 max-w-md"
            >
              {/* Caveat headline + hand-drawn underline */}
              <motion.div
                initial={{ y: -16, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.25 }}
                className="mb-7 relative"
              >
                <h1
                  style={{
                    fontFamily: "'Caveat', cursive",
                    color: '#0065F8',
                    fontWeight: 700,
                    fontSize: 'clamp(2.4rem, 5.5vw, 3.6rem)',
                    lineHeight: 1.0,
                    letterSpacing: '0.01em',
                    margin: 0,
                  }}
                >
                  Meet your new friend!
                </h1>
                <svg
                  width="220" height="14" viewBox="0 0 220 14"
                  style={{ display: 'block', margin: '6px auto 0' }}
                  aria-hidden="true"
                >
                  <path
                    d="M5 9 C 40 2, 80 12, 110 8 S 180 4, 215 9"
                    fill="none"
                    stroke="#FFD76A"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                </svg>
              </motion.div>

              {/* Character Image with sparkle ring + NEW sticker */}
              <motion.div
                initial={{ scale: 0, rotate: -8 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.35, type: 'spring', stiffness: 220, damping: 14 }}
                className="relative mb-6"
              >
                <div
                  className="relative"
                  style={{
                    width: 'clamp(180px, 26vw, 220px)',
                    height: 'clamp(180px, 26vw, 220px)',
                  }}
                >
                  {/* Rotating dotted sparkle ring */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 28, repeat: Infinity, ease: 'linear' }}
                    className="absolute"
                    style={{ inset: -14 }}
                  >
                    {[0, 60, 120, 180, 240, 300].map((deg, i) => {
                      const colors = ['#0065F8', '#FFD76A', '#FF6B9A', '#00CAFF', '#0065F8', '#FFD76A'];
                      return (
                        <div
                          key={i}
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            width: i % 2 === 0 ? 10 : 6,
                            height: i % 2 === 0 ? 10 : 6,
                            borderRadius: '50%',
                            background: colors[i],
                            transform: `translate(-50%, -50%) rotate(${deg}deg) translateY(-118px)`,
                            boxShadow: `0 2px 6px ${colors[i]}55`,
                          }}
                        />
                      );
                    })}
                  </motion.div>

                  {/* Avatar */}
                  <div
                    className="w-full h-full overflow-hidden"
                    style={{
                      borderRadius: '50%',
                      background: '#FFFFFF',
                      border: '4px solid #0065F8',
                      boxShadow: '0px 6px 0px #073B8E, 0px 18px 40px rgba(0,101,248,0.20)',
                    }}
                  >
                    <img
                      src={savedCharacterData.imageUrl}
                      alt={savedCharacterData.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                </div>
              </motion.div>

              {/* Character Name */}
              <motion.h2
                initial={{ y: 14, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                style={{
                  fontFamily: "'Fredoka', sans-serif",
                  color: '#0B1A33',
                  fontWeight: 700,
                  fontSize: 'clamp(1.75rem, 3.6vw, 2.4rem)',
                  letterSpacing: '-0.01em',
                  textTransform: 'uppercase',
                  margin: '0 0 8px',
                }}
              >
                {savedCharacterData.name}
              </motion.h2>

              {/* Description */}
              <motion.p
                initial={{ y: 14, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.55 }}
                style={{ fontFamily: "'Fredoka', sans-serif", color: '#5A6B85', fontSize: 15, lineHeight: 1.5, margin: '0 0 28px' }}
              >
                Saved to your library and ready for adventures!
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={{ y: 14, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.65 }}
                className="flex flex-col gap-3 w-full"
              >
                {/* Primary CTA - Create Story */}
                <button
                  onClick={() => {
                    setShowSuccessScreen(false);
                    navigate(`/create-story?character=${savedCharacterData.id}`);
                  }}
                  className="w-full px-6 py-4 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    background: '#0065F8',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: 16,
                    border: 'none',
                    borderRadius: 999,
                    boxShadow: '0px 5px 0px #073B8E, 0px 14px 32px rgba(0,101,248,0.20)',
                    cursor: 'pointer',
                  }}
                >
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  Create a Story with {savedCharacterData.name}
                </button>

                {/* Secondary CTAs */}
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowSuccessScreen(false);
                      setSavedCharacterData(null);
                      setAnimatedImageUrl(null);
                      setHanddrawnImageUrl(null);
                      setUploadedImage(null);
                      setUploadedImageUrl(null);
                      setInputMode(null);
                      setCharacterName("");
                      setShowAnimated(false);
                      setEditCount(0);
                      setCreatedCharacterId(null);
                    }}
                    className="flex-1 px-4 py-3 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                    style={{
                      fontFamily: "'Fredoka', sans-serif",
                      background: '#FFFFFF',
                      color: '#0065F8',
                      fontWeight: 700,
                      fontSize: 14,
                      border: '2px solid rgba(0,101,248,0.22)',
                      borderRadius: 999,
                      boxShadow: '0px 3px 0px rgba(11,26,51,0.06)',
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    Create Another
                  </button>
                  <button
                    onClick={() => {
                      setShowSuccessScreen(false);
                      navigate(`/audiobooks/create?character=${savedCharacterData.id}`);
                    }}
                    className="flex-1 px-4 py-3 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                    style={{
                      fontFamily: "'Fredoka', sans-serif",
                      background: '#FFFFFF',
                      color: '#334155',
                      fontWeight: 700,
                      fontSize: 14,
                      border: '2px solid rgba(11,26,51,0.08)',
                      borderRadius: 999,
                      boxShadow: '0px 3px 0px rgba(11,26,51,0.06)',
                      cursor: 'pointer',
                    }}
                  >
                    <svg width="16" height="16" fill="none" stroke="#0065F8" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 19.5A2.5 2.5 0 016.5 17H20" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
                    </svg>
                    Create Storybook
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CharacterLab;
